import { createTheme } from '@mui/material/styles';
import type { Shadows, ThemeOptions } from '@mui/material/styles';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import type { ColorMode } from './themeTypes';

/**
 * Reef Modernism — the Coral Creator brand identity.
 *
 * Every value here is extracted from the Claude Design project
 * *Creative Toolbox Mockups* (`Creative Toolbox Mockups.dc.html`), which
 * restyles the production layout — dashboard, swatch rails, lab shells,
 * Operations/Agents rail, chat drawer, settings — in reef modernism.
 *
 * The reference mode is **light**: every 1440px artboard in the file is drawn
 * on `#FAF8F5`. Dark is not a second design, it is the punctuation *inside*
 * that one — the mockups place near-black `#0D1117` panels (the canvas mat, the
 * agent drawer, the active operation chip, icon tiles) on a warm light ground.
 * The dark color mode derives from those same surfaces.
 *
 * What the mockups actually are, as a system:
 *
 *  - A **warm stone** ground, not a cool grey one and not sand. Paper
 *    `#FAF8F5`, recessed `#F4F0E9`, page `#EFEBE4`; body ink is warm brown-black
 *    `#3D3931` and secondary text is a warm grey, never a blue-grey.
 *  - **Terracotta `#E07A5F`** as the single accent: the primary CTA, the tab
 *    indicator, a checked switch, one emphasized stat tile.
 *  - **Selection is ink, not accent.** The active operation chip and the
 *    selected segment are `#0D1117` pills — the accent stays reserved for the
 *    action you are being invited to take.
 *  - **Pills.** 999px for buttons, chips and segments; 13px for cards.
 *  - **Two typefaces.** Space Grotesk carries the UI; IBM Plex Mono carries
 *    every micro-label, uppercase at .06–.1em tracking ("SWATCHES",
 *    "22 OPERATIONS", "CLOUD · FAL.AI"). That mono register is a large part of
 *    what makes the mockups look like themselves.
 *  - **Soft, warm shadows** — including a terracotta-tinted glow under the
 *    primary action. The mockups are lit, not printed.
 *
 * Three places the mockups' own values do not clear WCAG AA, corrected here at
 * the smallest possible distance and called out in the PR:
 *
 *  1. The CTA is drawn as white on `#E07A5F` — **2.95:1**. The fill hex is the
 *     brand signal, so it is preserved exactly and the label becomes the ink
 *     `#0D1117` (**6.42:1**).
 *  2. Accent *text* is drawn `#C9573C` — **4.03:1** on paper. Uses `#B0472E`
 *     (**5.23:1**), which is already in the mockups' palette.
 *  3. `#8A8580` carries much of the small copy — **3.45:1**. It stays for
 *     disabled states and non-text marks; `text.secondary` is `#6B665E`
 *     (**5.37:1**), the next step of the same warm ramp.
 */
export const reefColors = {
  // --- Accent -----------------------------------------------------------
  /** The brand terracotta, exact. Primary fill, tab indicator, checked switch. */
  terracotta: '#E07A5F',
  /** Hover fill — the mockups' deeper terracotta. */
  terracottaHover: '#C9573C',
  /** Pressed fill. */
  terracottaPress: '#B0472E',
  /**
   * Accent as TEXT / link / focus ring on the light grounds. 5.23:1 on paper,
   * 4.67:1 on stone — where the mockups' own `#C9573C` reaches only 4.03:1.
   */
  terracottaInk: '#B0472E',
  /**
   * Accent as TEXT on the dark surfaces. The raw brand hex reaches only
   * 4.36:1 on the raised slate, so dark text takes one step lighter — the
   * *fill* stays exactly `#E07A5F` everywhere.
   */
  terracottaLift: '#E58C74',
  /** Selected-row / emphasis tint on light. */
  terracottaWashLight: 'rgba(224, 122, 95, 0.10)',
  /** Selected-row tint on dark. */
  terracottaWashDark: 'rgba(224, 122, 95, 0.18)',
  /** The one glow in the theme: under the primary action, as drawn. */
  terracottaGlow: '0 4px 14px rgba(224, 122, 95, 0.35)',

  // --- Ink / dark surfaces (light-mode punctuation AND the dark ground) ---
  /** The near-black. Dark `background.default`; on light, the ink pill and mat. */
  ink: '#0D1117',
  /** One step off the ink — dark app bar, canvas checker's second square. */
  ink2: '#12161F',
  /** Dark `background.paper` — the mockups' panel navy-slate. */
  slate: '#1E2A3A',
  /** Dark raised layer — menus, dialogs, hovered rows. */
  slate2: '#2A3242',
  /** Dark rule / control boundary. */
  slate3: '#3A4152',
  /** Dark secondary boundary, lifted enough to bound a control. */
  slate4: '#5C6472',

  // --- Warm light surfaces ----------------------------------------------
  /** Light `background.paper` — the artboard ground of every mockup. */
  paper: '#FAF8F5',
  /** Recessed panel / rail fill. */
  paper2: '#F4F0E9',
  /** Light `background.default` — the page behind the panels. */
  stone: '#EFEBE4',
  /**
   * Sunken wells: inputs, code, scrollbar track. Lifted one step off the
   * mockups' `#E9E4DB`, which left secondary text at exactly 4.50:1 and the
   * control boundary at 2.89:1 against the well it sits in.
   */
  stone2: '#F0EBE3',

  // --- Warm ink ramp -----------------------------------------------------
  /** Light `text.primary`. 10.84:1 on paper. */
  warmInk: '#3D3931',
  /** Emphasis copy between primary and secondary. 6.75:1 on paper. */
  warmMid: '#5C5750',
  /** Light `text.secondary`. 5.37:1 on paper, 4.79:1 on stone. */
  warmSec: '#6B665E',
  /**
   * The mockups' most-used grey. 3.45:1 on paper, so it is *not* used for text
   * here — it carries disabled states, non-text marks and the control boundary
   * (where 3:1 is the bar and it clears it).
   */
  warmGrey: '#8A8580',
  /** Dark-mode `text.secondary`. 8.20:1 on ink. */
  warmDim: '#B0AAA0',
  /** Dark-mode `text.primary` — the paper carried into the dark. */
  paperInk: '#FAF8F5',

  // --- Lines -------------------------------------------------------------
  /** Softest divider, as drawn — decorative only, never bounds a control. */
  lineSoft: '#E3DED6',
  /** Standard divider / card border. */
  line: '#D0CCC7',
  /** Control boundary on light: inputs, outlined buttons. 3.45:1 on paper. */
  boundary: '#8A8580',
  /** Dark divider. */
  lineDark: '#2A3242',
  /** Control boundary on dark. Lifted from the mockups' `#5C6472`, which is
   *  2.43:1 on the panel slate and cannot bound a control. */
  boundaryDark: '#767E90',

  // --- Status (the mockups' own pill palette) ----------------------------
  infoInk: '#0B617C',
  infoTint: '#E3F2F7',
  info: '#0891B2',
  /** Lifted for dark: the mockups' `#0891B2` is 3.94:1 on the panel slate. */
  infoDark: '#22A8CA',
  successInk: '#256B4A',
  successTint: '#E8F3EC',
  /** Light-mode mark / dark-mode value pair. The dark one is lifted so it
   *  clears AA on the panel slate (the mockups' `#2E9E6B` is 4.30:1). */
  success: '#2E9E6B',
  successDark: '#41B37F',
  warningInk: '#6E5230',
  warning: '#E5A83D',
  /** Error stays clear of the accent by going redder — on dark it shifts to a
   *  rose so it never reads as a louder terracotta. */
  errorInk: '#9C4B36',
  errorDark: '#F2808C',

  // --- Scrims -------------------------------------------------------------
  scrimLight: 'rgba(13, 17, 23, 0.42)',
  scrimDark: 'rgba(9, 12, 17, 0.66)',

  // --- Signature chrome (ReefBackground) ---------------------------------
  reefWeaveLight: 'rgba(61, 57, 49, 0.06)',
  reefContourLight: 'rgba(61, 57, 49, 0.13)',
  reefContourAccentLight: 'rgba(224, 122, 95, 0.20)',
  reefWeaveDark: 'rgba(250, 248, 245, 0.05)',
  reefContourDark: 'rgba(250, 248, 245, 0.13)',
  reefContourAccentDark: 'rgba(224, 122, 95, 0.26)',

  /** The canvas transparency checker, as drawn in the mockups' dark mat. */
  canvasCheckerDark: '#12161F',
  /** Its light-mode counterpart, at the same visibility (1.4:1 on the well). */
  canvasCheckerLight: '#DCD5C8',
} as const;

const r = reefColors;

/** Pills for anything you press; a soft rectangle for anything you read. */
const RADIUS = { pill: 999, control: 9, card: 13 } as const;

/**
 * Warm, soft elevation. The mockups are lit rather than printed, so this is a
 * real blur ramp — but tinted with the ink rather than MUI's cool grey, and
 * paired with a hairline ring so a surface still has an edge at low elevation.
 * The whole 25-entry tuple is replaced so no stock grey shadow can leak in from
 * an `elevation={n}` elsewhere in the tree.
 */
function reefShadows(tint: string, ring: string): Shadows {
  const out: string[] = ['none'];
  for (let i = 1; i <= 24; i += 1) {
    const y = Math.min(1 + Math.round(i * 0.7), 18);
    const blur = Math.min(4 + Math.round(i * 2), 48);
    out.push(`0 0 0 1px ${ring}, 0 ${y}px ${blur}px -${Math.round(blur / 3)}px ${tint}`);
  }
  return out as unknown as Shadows;
}

// Both faces are self-hosted under reef-specific family names — see
// src/themes/reefFonts.css for why.
const SANS = [
  '"Space Grotesk Variable"',
  '"Space Grotesk"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(',');

const MONO = [
  '"IBM Plex Mono Reef"',
  '"IBM Plex Mono"',
  'ui-monospace',
  'SFMono-Regular',
  'Menlo',
  'Consolas',
  'monospace',
].join(',');

interface ReefModeTokens {
  bg: string;
  paper: string;
  raised: string;
  sunken: string;
  bar: string;
  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
  /** Divider weight — decorative, never bounds a control. */
  line: string;
  lineSoft: string;
  /** Control boundary — always ≥3:1 on every ground it can sit on. */
  boundary: string;
  scrim: string;
  focusRing: string;
  /** The label on a filled surface: ink on light fills, paper on dark ones. */
  onAccent: string;
  accentMain: string;
  accentWash: string;
  /** The ink pill that carries *selection*, and its label. */
  selectFill: string;
  selectText: string;
  error: string;
  warning: string;
  info: string;
  success: string;
  disabledFill: string;
  shadows: Shadows;
}

const LIGHT: ReefModeTokens = {
  bg: r.stone,
  paper: r.paper,
  raised: r.paper,
  sunken: r.stone2,
  bar: r.paper,
  textPrimary: r.warmInk,
  textSecondary: r.warmSec,
  textDisabled: r.warmGrey,
  line: r.line,
  lineSoft: r.lineSoft,
  boundary: r.boundary,
  scrim: r.scrimLight,
  focusRing: r.ink,
  onAccent: r.ink,
  accentMain: r.terracottaInk,
  accentWash: r.terracottaWashLight,
  selectFill: r.ink,
  selectText: r.paper,
  error: r.errorInk,
  warning: r.warningInk,
  info: r.infoInk,
  success: r.successInk,
  disabledFill: r.stone,
  shadows: reefShadows('rgba(13, 17, 23, 0.16)', 'rgba(13, 17, 23, 0.06)'),
};

const DARK: ReefModeTokens = {
  bg: r.ink,
  paper: r.slate,
  raised: r.slate2,
  sunken: r.ink2,
  bar: r.ink2,
  textPrimary: r.paperInk,
  textSecondary: r.warmDim,
  textDisabled: r.warmGrey,
  line: r.lineDark,
  lineSoft: r.lineDark,
  boundary: r.boundaryDark,
  scrim: r.scrimDark,
  focusRing: r.paperInk,
  onAccent: r.ink,
  accentMain: r.terracottaLift,
  accentWash: r.terracottaWashDark,
  selectFill: r.paperInk,
  selectText: r.ink,
  error: r.errorDark,
  warning: r.warning,
  info: r.infoDark,
  success: r.successDark,
  disabledFill: 'rgba(250, 248, 245, 0.08)',
  shadows: reefShadows('rgba(0, 0, 0, 0.55)', 'rgba(92, 100, 114, 0.22)'),
};

function makeReefTheme(mode: ColorMode) {
  const t = mode === 'dark' ? DARK : LIGHT;
  const isDark = mode === 'dark';

  /** The mono micro-label register the mockups use for every small caps line. */
  const monoLabel = {
    fontFamily: MONO,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
  };

  const options: ThemeOptions = {
    palette: {
      mode,
      primary: {
        main: t.accentMain,
        light: r.terracotta,
        dark: r.terracottaPress,
        // Never white: white on the brand terracotta is 2.95:1.
        contrastText: isDark ? r.ink : r.paper,
      },
      // Secondary is the ink, because in the mockups *selection* is an ink pill
      // and the accent is reserved for the action being invited.
      secondary: {
        main: t.selectFill,
        light: isDark ? r.warmDim : r.slate,
        dark: isDark ? r.paper : r.ink,
        contrastText: t.selectText,
      },
      background: { default: t.bg, paper: t.paper },
      text: { primary: t.textPrimary, secondary: t.textSecondary, disabled: t.textDisabled },
      divider: t.line,
      error: { main: t.error, contrastText: isDark ? r.ink : r.paper },
      warning: { main: t.warning, contrastText: isDark ? r.ink : r.paper },
      info: { main: t.info, contrastText: isDark ? r.ink : r.paper },
      success: { main: t.success, contrastText: isDark ? r.ink : r.paper },
      action: {
        active: t.textPrimary,
        hover: isDark ? 'rgba(250, 248, 245, 0.06)' : 'rgba(13, 17, 23, 0.04)',
        hoverOpacity: isDark ? 0.06 : 0.04,
        selected: t.accentWash,
        selectedOpacity: isDark ? 0.18 : 0.1,
        disabled: t.textDisabled,
        disabledBackground: isDark ? 'rgba(250, 248, 245, 0.08)' : 'rgba(13, 17, 23, 0.06)',
        disabledOpacity: 0.5,
        focus: isDark ? 'rgba(250, 248, 245, 0.14)' : 'rgba(13, 17, 23, 0.12)',
        focusOpacity: isDark ? 0.14 : 0.12,
      },
    },

    /**
     * Space Grotesk for everything you read as prose, IBM Plex Mono for
     * everything you read as a label. `overline` and `caption` carry the mono
     * register because that is what the mockups do with them.
     */
    typography: {
      fontFamily: SANS,
      h1: { fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15 },
      h2: { fontWeight: 700, letterSpacing: '-0.018em', lineHeight: 1.2 },
      h3: { fontWeight: 700, letterSpacing: '-0.014em', lineHeight: 1.25 },
      h4: { fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.3 },
      h5: { fontWeight: 600, letterSpacing: '-0.006em', lineHeight: 1.35 },
      h6: { fontWeight: 600, letterSpacing: '-0.004em', lineHeight: 1.4 },
      subtitle1: { fontWeight: 600, lineHeight: 1.5 },
      subtitle2: { fontWeight: 600, lineHeight: 1.5 },
      body1: { lineHeight: 1.65 },
      body2: { lineHeight: 1.6 },
      button: { fontWeight: 700, letterSpacing: '0.005em' },
      overline: { ...monoLabel, fontSize: '0.66rem', lineHeight: 1.6 },
      caption: { fontFamily: MONO, fontWeight: 500, letterSpacing: '0.04em' },
    },

    shape: { borderRadius: RADIUS.control },
    shadows: t.shadows,

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important',
            },
          },
          body: {
            backgroundColor: t.bg,
            scrollbarColor: `${t.boundary} ${t.sunken}`,
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': { width: 10, height: 10 },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: RADIUS.pill,
              backgroundColor: t.boundary,
              border: `3px solid ${t.sunken}`,
            },
            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
              backgroundColor: t.textSecondary,
            },
            '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
              backgroundColor: t.sunken,
            },
          },
          // Structural, never the accent: tabbing through a panel that has
          // already spent its terracotta should not add a second one.
          '*:focus-visible': { outline: `2px solid ${t.focusRing}`, outlineOffset: 2 },
          '::selection': {
            backgroundColor: r.terracottaWashDark,
            color: t.textPrimary,
          },
          'input::placeholder, textarea::placeholder': { opacity: 1, color: t.textSecondary },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: RADIUS.card, backgroundImage: 'none' },
          outlined: { borderColor: t.line },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.card,
            backgroundColor: t.paper,
            backgroundImage: 'none',
            border: `1px solid ${t.lineSoft}`,
            boxShadow: 'none',
            transition: 'border-color 160ms ease',
            '&:hover': { borderColor: t.line },
          },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: RADIUS.pill,
            padding: '9px 20px',
            transition: 'background-color 140ms ease, box-shadow 140ms ease',
          },
          // The single accent moment, as drawn: a terracotta pill with a soft
          // warm glow under it. The label is ink rather than the mockups' white
          // because white on this fill is 2.95:1; the fill hex is untouched.
          containedPrimary: {
            backgroundColor: r.terracotta,
            color: r.ink,
            boxShadow: r.terracottaGlow,
            '&:hover': { backgroundColor: r.terracottaHover, boxShadow: r.terracottaGlow },
            '&:active': { backgroundColor: r.terracottaPress, boxShadow: 'none' },
            '&.Mui-disabled': {
              backgroundColor: t.disabledFill,
              color: t.textDisabled,
              boxShadow: 'none',
            },
          },
          // Selection and secondary action are the ink pill.
          containedSecondary: {
            backgroundColor: t.selectFill,
            color: t.selectText,
            boxShadow: 'none',
            '&:hover': { backgroundColor: t.selectFill, filter: 'brightness(1.15)' },
          },
          outlined: {
            borderColor: t.boundary,
            color: t.textPrimary,
            '&:hover': {
              borderColor: t.textPrimary,
              backgroundColor: isDark ? 'rgba(250,248,245,0.06)' : 'rgba(13,17,23,0.04)',
            },
          },
          text: { color: t.accentMain },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: { borderRadius: RADIUS.pill, fontWeight: 600 },
          outlined: { borderColor: t.line, color: t.textPrimary },
          // An ink pill, matching the mockups' active operation chip.
          filledPrimary: { backgroundColor: t.selectFill, color: t.selectText },
          filledSecondary: { backgroundColor: t.selectFill, color: t.selectText },
          // A clickable chip's border is its affordance, so it clears 3:1.
          clickable: { '&.MuiChip-outlined': { borderColor: t.boundary } },
        },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: t.bar,
            color: t.textPrimary,
            borderBottom: `1px solid ${t.lineSoft}`,
            boxShadow: 'none',
          },
        },
      },
      MuiToolbar: { styleOverrides: { root: { backgroundImage: 'none' } } },

      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder, &::-webkit-input-placeholder, &::-moz-placeholder, &:-ms-input-placeholder, &::-ms-input-placeholder':
              { opacity: 1, color: t.textSecondary },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.control,
            backgroundColor: t.sunken,
            // By class, not by `fieldset`: MUI's own hover rule outranks the
            // element selector and the override would silently never apply.
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: t.boundary,
              borderWidth: 1,
            },
            [`&:hover .${outlinedInputClasses.notchedOutline}`]: { borderColor: t.textPrimary },
            [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: t.accentMain,
              borderWidth: 2,
            },
          },
        },
      },

      // Drawn terracotta in the mockups — a checked toggle is one of the few
      // places the accent is allowed outside the primary action.
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': { color: r.terracotta },
            '&.Mui-checked + .MuiSwitch-track': { backgroundColor: r.terracotta, opacity: 1 },
          },
          track: { backgroundColor: t.sunken, opacity: 1, border: `1px solid ${t.boundary}` },
        },
      },
      MuiSlider: {
        styleOverrides: {
          rail: { backgroundColor: t.sunken, opacity: 1, border: `1px solid ${t.boundary}` },
          track: { backgroundColor: t.accentMain, borderColor: t.accentMain },
          thumb: { backgroundColor: t.accentMain, border: `2px solid ${t.paper}` },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { backgroundColor: t.sunken, borderRadius: RADIUS.pill },
          bar: { backgroundColor: t.accentMain },
        },
      },

      MuiDivider: { styleOverrides: { root: { borderColor: t.lineSoft } } },
      MuiLink: {
        styleOverrides: {
          root: {
            color: t.accentMain,
            textDecoration: 'underline',
            textDecorationThickness: 1,
            textUnderlineOffset: 2,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: r.ink,
            color: r.paper,
            borderRadius: RADIUS.control,
            fontFamily: MONO,
            fontSize: '0.7rem',
            letterSpacing: '0.03em',
          },
          arrow: { color: r.ink },
        },
      },
      // The mockups set tabs in mono uppercase with a 2px accent underline.
      MuiTabs: { styleOverrides: { indicator: { backgroundColor: r.terracotta, height: 2 } } },
      MuiTab: {
        styleOverrides: {
          root: {
            ...monoLabel,
            fontSize: '0.72rem',
            color: t.textSecondary,
            '&.Mui-selected': { color: t.accentMain },
          },
        },
      },
      MuiAlert: { styleOverrides: { root: { borderRadius: RADIUS.control } } },
      MuiBackdrop: {
        styleOverrides: {
          root: { backgroundColor: t.scrim },
          // Must be restored alongside `root`: MUI sets the invisible variant's
          // transparency in its base style function, and theme overrides
          // serialize after it. Popover, Menu, Select and Autocomplete all
          // hard-code `invisible: true`, so without this every dropdown drops a
          // full-viewport scrim over the app.
          invisible: { backgroundColor: 'transparent' },
        },
      },
      MuiMenu: {
        styleOverrides: { paper: { backgroundColor: t.raised, border: `1px solid ${t.line}` } },
      },
      MuiPopover: {
        styleOverrides: { paper: { backgroundColor: t.raised, border: `1px solid ${t.line}` } },
      },
      MuiDialog: {
        styleOverrides: { paper: { backgroundColor: t.raised, border: `1px solid ${t.line}` } },
      },
    },
  };

  return createTheme(options);
}

/** Light — the reference mode. Every mockup artboard is drawn on this ground. */
export const reefModernismThemeLight = makeReefTheme('light');

/** Dark — built from the ink surfaces the mockups punctuate the light one with. */
export const reefModernismThemeDark = makeReefTheme('dark');
